'use client';

import type { IWordTiming } from '@repo/sdk';
import { type RefObject, useRef, useState } from 'react';
import type { IKaraokeCachedLoader } from './karaoke-cached-types';

interface IUseKaraokeAudio {
  audioRef: RefObject<HTMLAudioElement | null>;
  wordTimings: IWordTiming[];
  isPlaying: boolean;
  isLoading: boolean;
  toggle: () => void;
  audioHandlers: {
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
  };
}

export function useKaraokeAudio(loadAudio: IKaraokeCachedLoader): IUseKaraokeAudio {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timingsLoaded = useRef(false);
  const [wordTimings, setWordTimings] = useState<IWordTiming[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadTimings = async (): Promise<void> => {
    const result = await loadAudio();
    setIsLoading(false);
    if (result?.wordTimings) {
      setWordTimings(result.wordTimings);
    }
  };

  const run = (): void => {
    const el = audioRef.current;
    if (!el) {
      return;
    }
    if (!el.paused) {
      el.pause();
      el.currentTime = 0;
      return;
    }
    el.play().catch(() => undefined);
    if (!timingsLoaded.current) {
      timingsLoaded.current = true;
      setIsLoading(true);
      void loadTimings();
    }
  };

  return {
    audioRef,
    wordTimings,
    isPlaying,
    isLoading,
    toggle: () => void run(),
    audioHandlers: {
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onEnded: () => setIsPlaying(false),
    },
  };
}
